import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { usePWAStore } from '../../store/pwaStore';
import { useThemeStore } from '../../store/themeStore';
import { useTranslation } from 'react-i18next';
import { ProfileModal } from '../auth/ProfileModal';

interface HeaderProps {
  isMobile?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ isMobile = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const { toggleMobileSidebar } = usePWAStore();
  const { theme, setTheme } = useThemeStore();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const displayName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.first_name || user?.username || 'Account';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };

    if (isAccountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    if (showSavedToast) {
      const timer = setTimeout(() => setShowSavedToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSavedToast]);

  return (
    <>
    <header className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${isMobile ? 'h-12' : 'h-14'} flex items-center justify-between px-4 lg:px-6 safe-area-inset`}>
      {/* Left side - Hamburger for tablet gap (md–lg); logo on phone (tab bar's "More" opens the sidebar) */}
      <div className="flex items-center lg:hidden">
        {!isMobile && (
          <button
            onClick={toggleMobileSidebar}
            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        )}

        {/* Phone/tablet logo */}
        <button
          onClick={() => navigate('/dashboard')}
          className={`flex items-center ${!isMobile ? 'ml-2' : ''}`}
          aria-label="Go to dashboard"
        >
          <img src="/favicon.svg" alt="Auxilio Time" className={isMobile ? 'h-7 w-7' : 'h-8 w-8'} />
        </button>
      </div>

      {/* Desktop: empty left side for balance */}
      <div className="hidden lg:block" />

      {/* Right side - Actions */}
      <div className="flex items-center space-x-2">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5" />
          ) : (
            <SunIcon className="h-5 w-5" />
          )}
        </button>

        {/* My Account with Dropdown */}
        <div className="relative" ref={accountMenuRef}>
          <button
            onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
            title="Account"
          >
            <UserIcon className="h-5 w-5" />
            <span className="hidden md:inline">{displayName}</span>
          </button>

          {isAccountMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
              <button
                onClick={() => {
                  setIsAccountMenuOpen(false);
                  setIsProfileOpen(true);
                }}
                className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <UserCircleIcon className="h-5 w-5 mr-3" />
                My Profile
              </button>
              <div className="border-t border-gray-200 dark:border-gray-700" />
              <button
                onClick={() => {
                  setIsAccountMenuOpen(false);
                  handleLogout();
                }}
                className="flex items-center w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>

        {/* Logout button - visible on desktop */}
        <button
          onClick={handleLogout}
          className="hidden lg:flex items-center space-x-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
          title="Logout"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
          <span className="hidden md:inline">{t('nav.logout')}</span>
        </button>
      </div>
    </header>

    <ProfileModal
      isOpen={isProfileOpen}
      onClose={() => setIsProfileOpen(false)}
      onSaved={() => setShowSavedToast(true)}
    />

    {showSavedToast && (
      <div className="fixed top-4 right-4 z-[100] bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in">
        <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-medium">Profile saved successfully.</span>
      </div>
    )}
    </>
  );
};
